// Dedicated, single-responsibility render atoms for the LLM-facing DSL
// reference. Every doc surface — the shared `<ExprDslReference>`, the
// Storybook/DOM render, and the plugin-chat reconciler — composes
// these, so an op renders identically everywhere. Element vocabulary
// is intentionally limited to `<dl>/<dt>/<dd>/<pre>` (+ `<h2>/<p>`) so
// both React DOM and the markdown reconciler produce a uniform shape
// with no special-casing.
//
// Pure entry extraction lives in `op-entry.ts`; this module exports
// only components.

// Dedicated, single-responsibility render atoms for the LLM-facing DSL
// reference. Every doc surface — the shared `<ExprDslReference>`, the
// Storybook/DOM render, and the plugin-chat reconciler — composes
// these, so an op renders identically everywhere. Element vocabulary
// is intentionally limited to `<dl>/<dt>/<dd>/<pre>` (+ `<h2>/<p>`) so
// both React DOM and the markdown reconciler produce a uniform shape
// with no special-casing.
//
// Pure entry extraction lives in `op-entry.ts`; this module exports
// only components.

import React, { Fragment, type ReactNode } from 'react';

import { type Effect, exampleItems, type OpExample } from '../op-meta.ts';
import { type OpDocEntry, type OpDocScopeView, signatureOf } from './op-entry.ts';
import type { EnumDomainDoc } from './types.ts';
import type { KwargDoc } from './zod-walk.ts';

/**
 * Op name + effect tag. The tag is emitted *only* for `io` ops — `pure`
 * is the overwhelming default and tagging it everywhere is pure noise;
 * the only signal is which ops perform I/O.
 */
export function OpName({ name, effect }: { readonly name: string; readonly effect: Effect }): ReactNode {
  return (
    <>
      <code>{name}</code>
      {effect === 'io' ? ' (io)' : null}
    </>
  );
}

/**
 * Per-kwarg prose rows. Type + optionality already live in the
 * signature, so a row is emitted only when the kwarg carries a
 * non-trivial description — the novel semantics the signature can't
 * encode. No type echo, no "(optional)" boilerplate.
 */
export function OpKwargs({ kwargs }: { readonly kwargs: readonly KwargDoc[] }): ReactNode {
  return kwargs
    .filter((k) => k.description !== undefined && k.description.trim() !== '')
    .map((k) => `  \`:${k.name}\` — ${k.description}\n`);
}

export function OpExampleView({ example }: { readonly example: OpExample }): ReactNode {
  return exampleItems(example).map((it) => (
    <React.Fragment key={it.expr}>
      <code>{it.expr.replaceAll('\n', ' ')}</code>
      {!!it.result && <> → {it.result}</>}
    </React.Fragment>
  ));
}

/**
 * One op: a `<dt>` (name + effect) followed by a uniform sequence of
 * `<dd>` blocks (signature, description, optional type-note, documented
 * kwargs, examples). Always the same shape — no compact/expanded fork.
 */
export function OpEntry({ entry }: { readonly entry: OpDocEntry }): ReactNode {
  return (
    <li>
      {/* <OpName effect={entry.effect} name={entry.name} /> */}
      {signatureOf(entry)}
      {entry.description ? ` — ${entry.description}` : ''}
      {entry.typeNote ? ` (${entry.typeNote})` : ''}
      <OpKwargs kwargs={entry.kwargs} />
      {!!entry.example && <OpExampleView example={entry.example} />}
    </li>
  );
}

export function OpDocTable({ name, description, entries }: OpDocScopeView): ReactNode {
  return (
    <>
      <h2>{name}</h2>
      {description !== undefined && <p>{description}</p>}
      <ul>
        {entries.map((e) => (
          <OpEntry entry={e} key={e.name} />
        ))}
      </ul>
    </>
  );
}

/**
 * The single place enum values are spelled out. Signatures reference a
 * domain by name (`:color Color`); this glossary expands each domain
 * once — with per-member example docs when the codec supplies them.
 */
export function EnumDomains({ domains }: { readonly domains: readonly EnumDomainDoc[] }): ReactNode {
  return (
    <>
      <h2>Enum domains</h2>
      <p>Legal values for the symbol-valued kwargs above (referenced by domain name in signatures).</p>
      <dl>
        {domains.map((d) => (
          <Fragment key={d.name}>
            <dt>
              <code>{d.name}</code>
            </dt>
            <dd>
              {d.valueDocs === undefined
                ? d.typeLabel
                : Object.entries(d.valueDocs)
                    .map(([member, doc]) => `${member} (${doc})`)
                    .join(' | ')}
            </dd>
          </Fragment>
        ))}
      </dl>
    </>
  );
}
